import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import { getAuthorizedAxiosClient } from "../../common/axios"
import { selectToken } from "../users/reducer"
import { EVENT_TYPES } from "./candidate-details/data"
import {
  GoogleAuthProvider,
  MicrosoftProvider,
  ZohoAuthProvider,
} from "./components/ConfirmConnectEmailDialog"
import {
  NOTIFICATION_TYPES,
  excludeNotificationsByTypeAndIds,
  normalizeNotificationsPayload,
} from "./components/notificationTypes"

const retrieveDashboardMapData = createAsyncThunk(
  "customer/retrieveDasboardMapData",
  (_, thunkAPI) => {
    const token = selectToken(thunkAPI.getState())
    return getAuthorizedAxiosClient(token)
      .post("/clients/api/home/map/")
      .then(({ data }) => data)
  }
)

const retrieveEmailAuthorizationStatus = createAsyncThunk(
  "customer/retrieveEmailAuthorizationStatus",
  (_, thunkAPI) => {
    const token = selectToken(thunkAPI.getState())
    return getAuthorizedAxiosClient(token)
      .get("/workflows/api/email-provider-authorized/")
      .then(({ data }) => data)
  }
)

const retrieveCRMSettings = createAsyncThunk(
  "customer/retrieveCRMSettings",
  (_, thunkAPI) => {
    const token = selectToken(thunkAPI.getState())
    thunkAPI.dispatch(updateCRMSettingsLoading(true))
    return getAuthorizedAxiosClient(token)
      .get("/crm/api/offer-settings/")
      .then(({ data }) => data)
  }
)

const retrieveNotifications = createAsyncThunk(
  "customer/retrieveNotifications",
  (_, thunkAPI) => {
    const token = selectToken(thunkAPI.getState())
    return getAuthorizedAxiosClient(token)
      .get("/workflows/api/notifications/")
      .then(({ data }) => data)
  }
)

const markEventTransactionsRead = createAsyncThunk(
  "customer/markEventTransactionsRead",
  (event, thunkAPI) => {
    const token = selectToken(thunkAPI.getState())
    const eventType =
      [
        EVENT_TYPES.directEmail,
        EVENT_TYPES.email,
        EVENT_TYPES.emailReceiveEvent,
      ].indexOf(event.type) > -1
        ? "email"
        : "sms"
    return getAuthorizedAxiosClient(token)
      .put(
        `/workflows/api/notifications/${eventType}-event/${event.id}/mark-read/`
      )
      .then(() => event)
  }
)

const markCandidateImportJobRead = createAsyncThunk(
  "customer/markCandidateImportJobRead",
  (notification, thunkAPI) => {
    const token = selectToken(thunkAPI.getState())
    return getAuthorizedAxiosClient(token)
      .put(`/clients/api/candidates/import-jobs/${notification.id}/mark-read/`)
      .then(() => notification)
  }
)

const markUploadedLeadImportJobRead = createAsyncThunk(
  "customer/markUploadedLeadImportJobRead",
  (notification, thunkAPI) => {
    const token = selectToken(thunkAPI.getState())
    return getAuthorizedAxiosClient(token)
      .put(`/crm/api/uploaded-leads/import-jobs/${notification.id}/mark-read/`)
      .then(() => notification)
  }
)

const clearAllNotifications = createAsyncThunk(
  "customer/clearAllNotifications",
  (_, thunkAPI) => {
    const token = selectToken(thunkAPI.getState())
    return getAuthorizedAxiosClient(token)
      .put("/workflows/api/notifications/mark-all-read/")
      .then(() => true)
  }
)

const customerSlice = createSlice({
  name: "customer",
  initialState: {
    dashboardMapData: { status: "idle", value: null },
    emailAuth: {
      authorized: false,
      email: null,
      provider: null,
      expires_on: null,
      refresh_successful: false,
      loading: true,
    },
    // `loaded` is true only once the settings were fetched successfully, so
    // "no MLS" can be told apart from "not fetched yet" or "fetch failed".
    crmSettings: { settings: { mlss: [] }, loading: true, loaded: false },
    notifications: [],
    notificationsStatus: "idle",
    trialFinishedRoadblockDialogData: null,
    conciergeGreetingRequiredDialogOpen: false,
  },
  reducers: {
    updateCRMSettings(state, action) {
      state.crmSettings.settings = {
        ...state.crmSettings.settings,
        ...action.payload,
        mlss: action.payload.mlss ?? state.crmSettings.settings.mlss ?? [],
      }
    },
    updateCRMSettingsLoading(state, action) {
      state.crmSettings.loading = action.payload
    },
    openTrialFinishedRoadblockDialog(state, action) {
      state.trialFinishedRoadblockDialogData = action.payload.reduxVersion
    },
    closeTrialFinishedRoadblockDialog(state) {
      state.trialFinishedRoadblockDialogData = null
    },
    openConciergeGreetingRequiredDialog(state) {
      state.conciergeGreetingRequiredDialogOpen = true
    },
    closeConciergeGreetingRequiredDialog(state) {
      state.conciergeGreetingRequiredDialogOpen = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(retrieveDashboardMapData.pending, (state, action) => {
      state.dashboardMapData.status = "loading"
    })
    builder.addCase(retrieveDashboardMapData.fulfilled, (state, action) => {
      state.dashboardMapData.status = "loaded"
      state.dashboardMapData.value = action.payload
    })
    builder.addCase(
      retrieveEmailAuthorizationStatus.fulfilled,
      (state, { payload }) => {
        state.emailAuth = {
          ...payload,
          loading: false,
        }
      }
    )
    builder.addCase(retrieveCRMSettings.fulfilled, (state, { payload }) => {
      state.crmSettings = {
        loading: false,
        loaded: true,
        settings: {
          ...(payload || {}),
          mlss: payload?.mlss || [],
          commission_split_tiers: payload?.commission_split_tiers || [],
        },
      }
    })
    builder.addCase(retrieveCRMSettings.rejected, (state) => {
      state.crmSettings = {
        loading: false,
        loaded: state.crmSettings?.loaded ?? false,
        settings: {
          ...(state.crmSettings?.settings || {}),
          mlss: state.crmSettings?.settings?.mlss || [],
          commission_split_tiers:
            state.crmSettings?.settings?.commission_split_tiers || [],
        },
      }
    })
    builder.addCase(retrieveNotifications.pending, (state) => {
      if (state.notificationsStatus === "idle") {
        state.notificationsStatus = "loading"
      }
    })
    builder.addCase(retrieveNotifications.fulfilled, (state, { payload }) => {
      state.notifications = normalizeNotificationsPayload(payload)
      state.notificationsStatus = "loaded"
    })
    builder.addCase(retrieveNotifications.rejected, (state) => {
      if (state.notificationsStatus !== "loaded") {
        state.notificationsStatus = "error"
      }
    })
    builder.addCase(
      markEventTransactionsRead.fulfilled,
      (state, { payload: event }) => {
        const eventType =
          [
            EVENT_TYPES.directEmail,
            EVENT_TYPES.email,
            EVENT_TYPES.emailReceiveEvent,
          ].indexOf(event.type) > -1
            ? NOTIFICATION_TYPES.email
            : NOTIFICATION_TYPES.sms
        state.notifications = excludeNotificationsByTypeAndIds(
          state.notifications,
          eventType,
          event.transactions.map((tx) => tx.id)
        )
      }
    )
    builder.addCase(
      markCandidateImportJobRead.fulfilled,
      (state, { payload }) => {
        state.notifications = excludeNotificationsByTypeAndIds(
          state.notifications,
          NOTIFICATION_TYPES.candidateImport,
          [payload.id]
        )
      }
    )
    builder.addCase(
      markUploadedLeadImportJobRead.fulfilled,
      (state, { payload }) => {
        state.notifications = excludeNotificationsByTypeAndIds(
          state.notifications,
          NOTIFICATION_TYPES.uploadedLeadImport,
          [payload.id]
        )
      }
    )
    builder.addCase(clearAllNotifications.fulfilled, (state) => {
      state.notifications = []
    })
  },
})

const selectCustomer = (state) => state.customer
const selectDashboardMapData = (state) => selectCustomer(state).dashboardMapData
const selectEmailAuthorizationStatus = (state) =>
  selectCustomer(state).emailAuth
const selectEmailAuthorizationLoading = (state) =>
  selectEmailAuthorizationStatus(state).loading
const selectAuthorizedEmailProvider = (state) => {
  const providerID = selectEmailAuthorizationStatus(state).provider
  switch (providerID) {
    case GoogleAuthProvider.id:
      return GoogleAuthProvider
    case MicrosoftProvider.id:
      return MicrosoftProvider
    case ZohoAuthProvider.id:
      return ZohoAuthProvider
    default:
      return null
  }
}

const selectEmailProviderAuthorized = (state) => {
  const authStatus = selectEmailAuthorizationStatus(state)
  if (authStatus.loading) {
    return false
  }
  return authStatus.authorized && authStatus.refresh_successful
}
const selectAuthorizedEmailProviderRefreshFailed = (state) => {
  const emailAuthorizationStatus = selectEmailAuthorizationStatus(state)
  if (emailAuthorizationStatus.provider === ZohoAuthProvider.id) {
    return false
  }
  return (
    emailAuthorizationStatus.authorized &&
    !emailAuthorizationStatus.refresh_successful
  )
}
const selectCRMSettings = (state) =>
  selectCustomer(state).crmSettings ?? {
    settings: { mlss: [] },
    loading: true,
    loaded: false,
  }
const selectCRMSettingsLoading = (state) => selectCRMSettings(state).loading
const selectCRMSettingsData = (state) =>
  selectCRMSettings(state).settings ?? { mlss: [] }
const selectCommissionTiers = (state) =>
  selectCRMSettingsData(state).commission_split_tiers || []
const selectSelectedMLSID = (state) =>
  selectCRMSettingsData(state).mlss?.[0]?.id
const selectNotifications = (state) => selectCustomer(state).notifications
const selectNotificationsStatus = (state) =>
  selectCustomer(state).notificationsStatus || "idle"
const selectTrialFinishedRoadblockDialogData = (state) =>
  selectCustomer(state).trialFinishedRoadblockDialogData
const selectConciergeGreetingRequiredDialogOpen = (state) =>
  selectCustomer(state).conciergeGreetingRequiredDialogOpen

export default customerSlice.reducer

export const {
  updateCRMSettings,
  updateCRMSettingsLoading,
  openTrialFinishedRoadblockDialog,
  closeTrialFinishedRoadblockDialog,
  openConciergeGreetingRequiredDialog,
  closeConciergeGreetingRequiredDialog,
} = customerSlice.actions

export {
  retrieveDashboardMapData,
  retrieveEmailAuthorizationStatus,
  retrieveCRMSettings,
  retrieveNotifications,
  markEventTransactionsRead,
  markCandidateImportJobRead,
  markUploadedLeadImportJobRead,
  clearAllNotifications,
  selectDashboardMapData,
  selectEmailAuthorizationStatus,
  selectEmailAuthorizationLoading,
  selectEmailProviderAuthorized,
  selectAuthorizedEmailProvider,
  selectAuthorizedEmailProviderRefreshFailed,
  selectCRMSettings,
  selectCRMSettingsLoading,
  selectCommissionTiers,
  selectSelectedMLSID,
  selectNotifications,
  selectNotificationsStatus,
  selectTrialFinishedRoadblockDialogData,
  selectConciergeGreetingRequiredDialogOpen,
}
